export type SendOutcome<T, F> =
  | { kind: "done"; value: T }
  | { kind: "refused"; failure: F }
  | { kind: "unclear"; failure: F };

export type Lookup<T> = { kind: "found"; value: T } | { kind: "absent" } | { kind: "unknown" };

export type LandedWrite<T, F> =
  | { ok: true; value: T }
  | { ok: false; failure: F; unconfirmed: boolean };

export interface WriteOnce<T, F, P> {
  key: string;
  group: string;
  settleMs: number;
  unknownSettles: boolean;
  send: (pending: (proof: P) => void) => Promise<SendOutcome<T, F>>;
  lookup: (startedAt: number, proof: P) => Promise<Lookup<T>>;
}

export function writeOnce<T, F, P>(write: WriteOnce<T, F, P>): Promise<LandedWrite<T, F>> {
  const known = IN_FLIGHT.get(write.key);
  if (known) return known.promise as Promise<LandedWrite<T, F>>;
  const ahead = [...IN_FLIGHT.values()]
    .filter((flight) => flight.group === write.group)
    .map((flight) => flight.promise);
  const started = run(write, ahead).finally(() => {
    if (IN_FLIGHT.get(write.key)?.promise === started) IN_FLIGHT.delete(write.key);
  });
  IN_FLIGHT.set(write.key, { group: write.group, promise: started });
  return started;
}

export function forgetPendingWrites(): void {
  IN_FLIGHT.clear();
  PENDING.clear();
}

async function run<T, F, P>(
  write: WriteOnce<T, F, P>,
  ahead: readonly Promise<unknown>[],
): Promise<LandedWrite<T, F>> {
  await Promise.allSettled(ahead);

  for (const [key, pending] of [...PENDING.entries()]) {
    if (pending.group !== write.group) continue;
    const seen = await watch(pending);
    const own = key === write.key;
    if (seen.kind === "found") {
      PENDING.delete(key);
      if (own) return { ok: true, value: seen.value as T };
      continue;
    }
    if (!settled(pending, seen)) {
      return { ok: false, failure: pending.failure as F, unconfirmed: true };
    }
    PENDING.delete(key);
  }

  const startedAt = Date.now();
  let proof: { held: P } | null = null;
  const sent = await write.send((held) => {
    proof = { held };
  });
  if (sent.kind === "done") return { ok: true, value: sent.value };
  if (sent.kind === "refused" || proof === null) {
    return { ok: false, failure: sent.failure, unconfirmed: false };
  }

  const unclear: Pending = {
    group: write.group,
    startedAt,
    settlesBy: startedAt + write.settleMs,
    unknownSettles: write.unknownSettles,
    failure: sent.failure,
    proof: (proof as { held: P }).held,
    lookup: write.lookup as Pending["lookup"],
  };
  PENDING.set(write.key, unclear);
  const seen = await watch(unclear);
  if (seen.kind !== "found") return { ok: false, failure: sent.failure, unconfirmed: true };
  PENDING.delete(write.key);
  return { ok: true, value: seen.value as T };
}

async function watch(pending: Pending): Promise<Lookup<unknown>> {
  const until = Math.min(Date.now() + WATCH_MS, pending.settlesBy);
  for (;;) {
    const seen = await lookupWithin(pending);
    if (seen.kind === "found" || Date.now() >= until) return seen;
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
}

async function lookupWithin(pending: Pending): Promise<Lookup<unknown>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<Lookup<unknown>>((resolve) => {
    timer = setTimeout(() => resolve({ kind: "unknown" }), LOOKUP_TIMEOUT_MS);
  });
  try {
    return await Promise.race([pending.lookup(pending.startedAt, pending.proof), timedOut]);
  } catch {
    return { kind: "unknown" };
  } finally {
    clearTimeout(timer);
  }
}

function settled(pending: Pending, seen: Lookup<unknown>): boolean {
  if (Date.now() < pending.settlesBy) return false;
  return seen.kind === "absent" || pending.unknownSettles;
}

interface Pending {
  group: string;
  startedAt: number;
  settlesBy: number;
  unknownSettles: boolean;
  failure: unknown;
  proof: unknown;
  lookup: (startedAt: number, proof: unknown) => Promise<Lookup<unknown>>;
}

const IN_FLIGHT = new Map<
  string,
  { group: string; promise: Promise<LandedWrite<unknown, unknown>> }
>();
const PENDING = new Map<string, Pending>();
const WATCH_MS = 90_000;
const POLL_MS = 10_000;
const LOOKUP_TIMEOUT_MS = 75_000;
